import { useLocalization } from '@lib/localization';
import { useProjectRoute } from '@lib/routing/useProjectRoute';
import type SPInspection from '@lib/spproject/SPInspection';
import type { Inspection } from '@lib/spproject/types';
import { useAppSelector } from '@store/hooks';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CanvasTexture, Sprite, SpriteMaterial } from 'three';
import { PoiCanvas } from './PoiCanvas';
import { PoiSprite, type SpriteUserData } from './PoiSprite';
import { PoiTooltip } from './PoiTooltip';

interface PoiProps {
  inspection: SPInspection | null;
}
const PoiList = memo(({ inspection }: PoiProps) => {
  const { project } = useAppSelector((state) => state.viewer);
  const navigate = useNavigate();
  const { buildPath } = useProjectRoute();
  const [hoveredPoint, setHoveredPoint] = useState<string | null>(null);
  const [sprites, setSprites] = useState<Sprite[]>([]);

  const { t } = useLocalization();

  const noticeColors = useNoticeColors();
  const staticNoticeColors = useRef(noticeColors);

  const getPoiHash = (poi: Inspection.PointOfInterest): string => {
    const stepIndex = poi.stepIndex;
    const noticeType = poi.steps?.[stepIndex]?.noticeType;
    return `${poi.id}-${stepIndex}-${noticeType}`;
  };

  const spriteHash = useMemo(() => {
    return inspection?.inspection.pointOfInterests?.map(getPoiHash).join(',');
  }, [inspection?.inspection.pointOfInterests]);

  useEffect(() => {
    if (!inspection?.inspection.pointOfInterests?.length) {
      setSprites([]);
      return;
    }

    const newSprites: Sprite[] = [];

    for (const poi of inspection.inspection.pointOfInterests) {
      const currentStep = poi.steps?.[poi.stepIndex ?? 0];
      const noticeType = currentStep?.noticeType ?? 'NotChecked';
      const color = staticNoticeColors.current[noticeType];

      const canvas = PoiCanvas(poi.order.toString(), color);
      const texture = new CanvasTexture(canvas);

      texture.generateMipmaps = false;
      texture.needsUpdate = true;

      const material = new SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        sizeAttenuation: false,
        opacity: 0.95
      });

      const sprite = new Sprite(material);
      sprite.position.set(-poi.position.x, poi.position.y + 0.02, poi.position.z);
      sprite.scale.set(0.05, 0.05, 0.05);
      sprite.userData = {
        poi,
        id: poi.id,
        name: poi.name,
        noticeType
      } as SpriteUserData;

      newSprites.push(sprite);
    }

    setSprites(newSprites);

    return () => {
      newSprites.forEach((sprite) => {
        sprite.material.map?.dispose();
        sprite.material.dispose();
        sprite.removeFromParent();
      });
    };
  }, [spriteHash, inspection?.inspection.pointOfInterests]);

  const handlePointClick = useCallback(
    (poi: Inspection.PointOfInterest) => {
      if (!inspection || !project) return;
      const poiRoute = buildPath({
        inspectionId: inspection.inspection.info.id,
        poiId: poi.id,
        stepIndex: poi.stepIndex ?? 0
      });
      navigate(poiRoute);
    },
    [inspection, project, navigate, buildPath]
  );

  const handleHover = useCallback((id: string | null) => setHoveredPoint(id), []);

  if (!inspection?.inspection.pointOfInterests?.length) return null;

  const hoveredPoi = inspection.inspection.pointOfInterests.find((p) => p.id === hoveredPoint);

  return (
    <>
      <group name="inspection-points">
        {sprites.map((sprite) => {
          const userData = sprite.userData as SpriteUserData;
          return (
            <PoiSprite
              key={userData.id}
              sprite={sprite}
              onPointClick={handlePointClick}
              onHover={handleHover}
              isHovered={hoveredPoint === userData.id}
            />
          );
        })}
      </group>
      <PoiTooltip hoveredPoi={hoveredPoi} t={t} />
    </>
  );
});

export default PoiList;
